import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SelectionModal } from "../../../components/SelectionModal";
import { useAuth } from "../../../providers/AuthProvider";
import { createTopUpSession } from "../api/billingApi";
import type { TopUpModalController } from "../hooks/useTopUpModal";
import { BILLING_TOPUP_AUTH_KEY } from "../constants";
import { createLogger } from "../../../utils/logger";

const logger = createLogger("BillingTopUpModal");

interface BillingTopUpModalProps {
  controller: TopUpModalController;
  onCompleted?: () => void;
}

const PRESET_AMOUNTS = [10, 25, 50, 100];

const modalContentClasses = "flex flex-col gap-6";
const presetsGridClasses = "grid grid-cols-4 gap-3";
const presetButtonClasses = "rounded-xl border px-2 py-3 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900";
const presetInactiveClasses = "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";
const presetActiveClasses = "border-sky-600 bg-sky-50 text-sky-700 ring-1 ring-sky-600 dark:bg-sky-900/30 dark:text-sky-300 dark:ring-sky-500 dark:border-sky-500";
const inputLabelClasses = "flex flex-col gap-2 text-sm font-medium text-slate-700 dark:text-slate-300";
const inputClasses = "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500";
const errorClasses = "rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 border border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800";
const successClasses = "rounded-xl bg-green-50 px-4 py-3 text-sm text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800";
const hintClasses = "text-sm text-slate-500 italic text-center dark:text-slate-400";
const actionsClasses = "flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800";
const primaryButtonClasses = "pm-btn pm-btn-primary";
const secondaryButtonClasses = "pm-btn pm-btn-surface";

export function BillingTopUpModal({
  controller,
  onCompleted
}: BillingTopUpModalProps) {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkoutInitiated, setCheckoutInitiated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!controller.isOpen) {
    return null;
  }

  const handleSubmit = async () => {
    if (!accessToken) {
      setError(t("billing.topup.errors.auth"));
      return;
    }
    setIsSubmitting(true);
    setError(null);
    setCheckoutInitiated(false);
    try {
      const idempotencyKey =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      const session = await createTopUpSession(
        accessToken,
        controller.selectedCredits,
        idempotencyKey
      );
      setCheckoutInitiated(true);
      try {
        localStorage.setItem(BILLING_TOPUP_AUTH_KEY, accessToken);
      } catch (error) {
        logger.warn("Failed to persist top-up auth token", error);
      }
      window.location.assign(session.checkout_url);
      onCompleted?.();
    } catch (err) {
      logger.error("Failed to create top up session", err);
      setError(
        err instanceof Error ? err.message : t("billing.topup.errors.generic")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const approxLots = Math.max(1, Math.floor(controller.selectedCredits / 10));

  return (
    <SelectionModal
      title={t("billing.topup.title")}
      closeLabel={t("actions.close")}
      onClose={controller.close}
    >
      <div className={modalContentClasses}>
        <p className="text-sm text-slate-600 dark:text-slate-400">{t("billing.topup.copy")}</p>
        <div className={presetsGridClasses}>
          {PRESET_AMOUNTS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={`${presetButtonClasses} ${
                controller.selectedCredits === preset
                  ? presetActiveClasses
                  : presetInactiveClasses
              }`}
              onClick={() => controller.setSelectedCredits(preset)}
            >
              {preset} {t("billing.card.credits_label")}
            </button>
          ))}
        </div>
        <label className={inputLabelClasses}>
          <span>{t("billing.topup.custom_label")}</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            className={inputClasses}
            value={controller.selectedCredits}
            onChange={(event) =>
              controller.setSelectedCredits(Math.max(1, Number(event.target.value)))
            }
          />
        </label>
        {error ? (
          <p className={errorClasses} role="alert">
            {error}
          </p>
        ) : null}
        {checkoutInitiated ? (
          <p className={successClasses}>
            {t("billing.topup.redirect_notice")}
          </p>
        ) : (
          <p className={hintClasses}>
            {t("billing.topup.hint", {
              credits: controller.selectedCredits,
              lots: approxLots
            })}
          </p>
        )}
        <div className={actionsClasses}>
          <button
            type="button"
            className={secondaryButtonClasses}
            onClick={controller.close}
          >
            {t("actions.cancel")}
          </button>
          <button
            type="button"
            className={primaryButtonClasses}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t("common.loading") : t("billing.card.top_up")}
          </button>
        </div>
      </div>
    </SelectionModal>
  );
}
